<template>
  <system-frame title="E-post" hint="Saatjad, Resendi saatmisdomeen, kvoot ja saadetud kirjad koos nende olekuga.">

    <!-- The read key is missing or send-only: say so once, at the top. -->
    <v-alert v-if="healthError" type="warning" variant="tonal" density="compact" class="mb-6">
      {{ healthError }}
    </v-alert>

    <!-- Saatjad: which sender the letters leave by, the backup, and the last
         30 days from intra's own log — works whichever sender is main. -->
    <card v-if="senders" class="mb-6">
      <div class="d-flex align-center flex-wrap ga-2">
        <div class="text-h6">Saatjad</div>
        <v-chip size="small" variant="flat" color="primary">Saatja: {{ senderName(senders.provider) }}</v-chip>
        <v-chip size="small" variant="outlined" :color="senders.backup ? 'green-darken-1' : 'grey-darken-1'">
          Varusaatja: {{ senders.backup ? senderName(senders.backup) : 'väljas' }}
        </v-chip>
      </div>
      <p v-if="senders.provider !== 'resend'" class="text-body-2 text-grey-darken-1 mt-3 mb-0">
        Kirjad lähevad {{ senderName(senders.provider) }} kaudu — allpool olevad Resendi andmed näitavad ainult Resendi kaudu saadetud kirju.
      </p>
      <div class="text-body-2 text-grey-darken-1 mt-3">Viimased 30 päeva intra logi järgi:</div>
      <div v-if="!Object.keys(senders.log.providers).length" class="text-grey-darken-1 mt-1">Logis kirju veel ei ole.</div>
      <div v-else class="d-flex flex-wrap ga-6 mt-2">
        <div v-for="(row, name) in senders.log.providers" :key="name">
          <div class="text-subtitle-2">{{ senderName(name) }}</div>
          <div class="text-body-2">
            Saadetud <span class="num">{{ count(row.sent) }}</span><template v-if="row.byBackup"> (varusaatjana {{ count(row.byBackup) }})</template>
            · Ebaõnnestus <span class="num" :class="{ 'text-red-darken-1': row.failed }">{{ count(row.failed) }}</span>
          </div>
        </div>
      </div>
      <div v-if="senders.log.failures.length" class="mt-4">
        <div class="text-subtitle-2">Viimased ebaõnnestumised</div>
        <div v-for="(f, i) in senders.log.failures" :key="i" class="text-body-2 mt-1 failure-row">
          <span class="text-no-wrap">{{ formatTime(f.at) }}</span> · {{ senderName(f.provider) }} · {{ f.recipient }} — <span class="text-grey-darken-1">{{ f.error }}</span>
        </div>
      </div>
    </card>

    <v-row>
      <!-- Sending domains: is the mail allowed to leave as gaspre.ee? -->
      <v-col cols="12" md="7">
        <card class="h-100">
          <div class="d-flex align-center">
            <div class="text-h6">Saatmise domeen</div>
            <v-spacer />
            <v-btn variant="text" size="small" :loading="healthLoading" @click="loadHealth">
              <v-icon start>mdi-refresh</v-icon>Värskenda
            </v-btn>
          </div>
          <div v-if="health && health.domainsError" class="text-grey-darken-1 mt-2">Domeene ei õnnestunud lugeda: {{ health.domainsError }}</div>
          <div v-else-if="health && !health.domains.length" class="text-grey-darken-1 mt-2">Resendis ei ole ühtegi domeeni.</div>
          <div v-for="domain in (health ? health.domains : [])" :key="domain.id" class="mt-3">
            <div class="d-flex align-center flex-wrap ga-2">
              <span class="text-subtitle-1 font-weight-medium">{{ domain.name }}</span>
              <v-chip size="small" variant="flat" :color="domainState(domain.status).color">
                <v-icon start size="small">{{ domainState(domain.status).icon }}</v-icon>{{ domainState(domain.status).text }}
              </v-chip>
              <span class="text-body-2 text-grey-darken-1">{{ domain.region }}</span>
            </div>
            <div class="d-flex flex-wrap ga-2 mt-2">
              <v-chip
                v-for="(record, i) in domain.records"
                :key="i"
                size="x-small"
                variant="outlined"
                :color="record.status === 'verified' ? 'green-darken-2' : (record.status === 'failed' ? 'red-darken-2' : 'grey-darken-1')"
              >
                <v-icon start size="x-small">{{ record.status === 'verified' ? 'mdi-check' : (record.status === 'failed' ? 'mdi-close' : 'mdi-timer-sand') }}</v-icon>
                {{ record.record }} · {{ record.type }}
              </v-chip>
            </div>
            <div class="text-body-2 text-grey-darken-1 mt-2">
              Avamiste jälgimine: {{ domain.openTracking ? 'sees' : 'väljas' }} ·
              Klikkide jälgimine: {{ domain.clickTracking ? 'sees' : 'väljas' }}
              <template v-if="!domain.openTracking"> — avamiste arvu ei ole võimalik näidata</template>
            </div>
          </div>
        </card>
      </v-col>

      <!-- Quota: what is used against what the plan allows. -->
      <v-col cols="12" md="5">
        <card class="h-100">
          <div class="text-h6">Kvoot</div>
          <div v-if="health && !health.used" class="text-grey-darken-1 mt-3">Kvooti ei õnnestunud lugeda: {{ health.usedError }}</div>
          <template v-else-if="health">
            <div class="mt-3">
              <div class="d-flex justify-space-between text-body-1">
                <span>Sel kuul</span>
                <span class="num">{{ count(health.used.month) }}<template v-if="health.limits.monthly"> / {{ count(health.limits.monthly) }}</template></span>
              </div>
              <v-progress-linear
                v-if="health.limits.monthly"
                :model-value="pct(health.used.month, health.limits.monthly)"
                :color="quotaColor(health.used.month, health.limits.monthly)"
                height="8"
                rounded
                class="mt-1"
              />
            </div>
            <div v-if="health.limits.daily" class="mt-4">
              <div class="d-flex justify-space-between text-body-1">
                <span>Täna</span>
                <span class="num">{{ count(health.used.today) }} / {{ count(health.limits.daily) }}</span>
              </div>
              <v-progress-linear
                :model-value="pct(health.used.today, health.limits.daily)"
                :color="quotaColor(health.used.today, health.limits.daily)"
                height="8"
                rounded
                class="mt-1"
              />
            </div>
            <p class="text-body-2 text-grey-darken-1 mt-4 mb-0">
              Resend loeb kvooti UTC kalendrikuu ja -päeva järgi.
              <template v-if="!health.limits.monthly">
                Paketi piiri saab sisestada: Süsteem → Seaded → E-post (Resend).
              </template>
            </p>
          </template>
          <div v-else class="text-grey mt-3">—</div>
        </card>
      </v-col>
    </v-row>

    <!-- The chosen days: the totals, the per-day chart and the list all follow them. -->
    <card class="mt-6">
      <div class="d-flex align-center flex-wrap ga-4">
        <date-field v-model="from" label="Alates" density="compact" variant="outlined" hide-details style="max-width: 190px" />
        <date-field v-model="to" label="Kuni" density="compact" variant="outlined" hide-details style="max-width: 190px" />
        <v-btn color="primary" :loading="metricsLoading || (loading && !emails.length)" @click="loadRange">Näita</v-btn>
      </div>
      <v-alert v-if="metricsError" type="warning" variant="tonal" density="compact" class="mt-4 mb-0">{{ metricsError }}</v-alert>

      <div class="tiles mt-4">
        <div v-for="tile in tiles" :key="tile.label" class="tile">
          <div class="tile-label">{{ tile.label }}</div>
          <div class="tile-value num">{{ tile.value }}</div>
          <div v-if="tile.note" class="tile-note">{{ tile.note }}</div>
        </div>
      </div>

      <!-- Sent per day, one series. Days with a bounce or failure carry a status
           marker (icon + count) rather than a second colour: green against red
           is the pair colour-blind readers cannot tell apart. -->
      <div v-if="chart.days.length" class="chart-wrap mt-4" @mouseleave="hover = null">
        <svg :viewBox="'0 0 ' + chart.width + ' ' + chart.height" class="chart" role="img" :aria-label="'Saadetud e-kirjad päevade kaupa, ' + from + ' – ' + to">
          <g v-for="tick in chart.ticks" :key="'t' + tick.value">
            <line :x1="chart.left" :x2="chart.width - 8" :y1="tick.y" :y2="tick.y" class="grid" />
            <text :x="chart.left - 6" :y="tick.y + 4" text-anchor="end" class="axis">{{ count(tick.value) }}</text>
          </g>
          <g v-for="(day, i) in chart.days" :key="day.period">
            <rect
              :x="day.x - chart.slot / 2"
              :y="chart.top"
              :width="chart.slot"
              :height="chart.base - chart.top"
              fill="transparent"
              @mouseenter="hover = i"
            />
            <path v-if="day.sent" :d="barPath(day.x - chart.bar / 2, day.y, chart.bar, chart.base - day.y)" class="bar" :class="{ dim: hover !== null && hover !== i }" pointer-events="none" />
            <g v-if="day.problems" pointer-events="none">
              <circle :cx="day.x" :cy="day.y - 12" r="6" class="problem" />
              <text :x="day.x" :y="day.y - 9" text-anchor="middle" class="problem-mark">!</text>
            </g>
            <text v-if="chart.labelEvery && i % chart.labelEvery === 0" :x="day.x" :y="chart.base + 16" text-anchor="middle" class="axis">{{ shortDay(day.period) }}</text>
          </g>
          <line :x1="chart.left" :x2="chart.width - 8" :y1="chart.base" :y2="chart.base" class="baseline" />
        </svg>
        <div v-if="hover !== null && chart.days[hover]" class="tip" :style="tipStyle">
          <div class="font-weight-medium">{{ longDay(chart.days[hover].period) }}</div>
          <div>Saadetud: <span class="num">{{ chart.days[hover].sent }}</span></div>
          <div>Kohale jõudnud: <span class="num">{{ chart.days[hover].delivered }}</span></div>
          <div v-if="chart.days[hover].problems"><span class="problem-key"></span>Põrkus või ebaõnnestus: <span class="num">{{ chart.days[hover].problems }}</span></div>
        </div>
        <div class="text-body-2 text-grey-darken-1 mt-1"><span class="problem-key"></span>päeval oli põrkunud või ebaõnnestunud e-kirju</div>
      </div>
    </card>

    <card class="mt-6">
      <div class="text-h6">Saadetud e-kirjad</div>
      <div class="d-flex align-center flex-wrap ga-4 mt-3">
        <v-select
          v-model="statuses"
          :items="statusOptions"
          label="Olek"
          placeholder="Kõik"
          persistent-placeholder
          multiple
          chips
          closable-chips
          clearable
          density="compact"
          variant="outlined"
          hide-details
          style="min-width: 220px; max-width: 460px"
        />
        <span v-if="emails.length" class="text-body-2 text-grey-darken-1">
          {{ shown.length }} e-kirja<template v-if="statuses.length"> ({{ emails.length }}-st)</template>
        </span>
      </div>
      <v-alert v-if="statuses.length && next && !loading" type="info" variant="tonal" density="compact" class="mt-4 mb-0">
        Valitud päevadel on rohkem e-kirju, kui korraga laaditakse ({{ emails.length }}) — oleku filter
        arvestab ainult neid. Kitsenda kuupäevi või vajuta „Laadi veel”.
      </v-alert>
      <v-alert v-if="error" type="warning" variant="tonal" density="compact" class="mt-4 mb-0">{{ error }}</v-alert>
      <v-data-table
        :headers="headers"
        :items="shown"
        :loading="loading"
        :items-per-page="-1"
        hide-default-footer
        item-value="id"
        :no-data-text="error ? '' : (statuses.length && emails.length ? 'Selle olekuga e-kirju valitud päevadel ei ole' : 'Valitud päevadel e-kirju ei saadetud')"
        class="mt-2 mail-table"
        hover
        @click:row="(e, { item }) => open(item.id)"
      >
        <template v-slot:[`item.createdAt`]="{ value }">
          <span class="text-no-wrap">{{ formatDate(value) }}</span>
        </template>
        <template v-slot:[`item.to`]="{ value }">
          <span>{{ value.join(', ') }}</span>
        </template>
        <template v-slot:[`item.lastEvent`]="{ value }">
          <v-chip size="x-small" variant="flat" :color="eventState(value).color">{{ eventState(value).text }}</v-chip>
        </template>
      </v-data-table>
      <div v-if="next" class="text-center pt-4">
        <v-btn variant="outlined" :loading="loading" @click="loadMore">Laadi veel</v-btn>
      </div>
    </card>

    <card class="mt-6">
      <div class="text-h6">Blokeeritud aadressid</div>
      <p class="text-grey-darken-1 mt-2 mb-0">
        Aadressid, millele Resend enam ei saada — kiri põrkus püsivalt tagasi või saaja
        märkis selle rämpspostiks. Intrast saadetud kiri neile ei jõua kohale.
        Bloki eemaldamine käib Resendi keskkonnas.
      </p>
      <v-alert v-if="blockedError" type="warning" variant="tonal" density="compact" class="mt-4 mb-0">{{ blockedError }}</v-alert>
      <v-data-table
        :headers="blockedHeaders"
        :items="blocked"
        :loading="blockedLoading"
        :items-per-page="-1"
        hide-default-footer
        item-value="id"
        :no-data-text="blockedError ? '' : 'Blokeeritud aadresse ei ole'"
        class="mt-2"
      >
        <template v-slot:[`item.origin`]="{ value }">{{ originText(value) }}</template>
        <template v-slot:[`item.createdAt`]="{ value }">
          <span class="text-no-wrap">{{ formatDate(value) }}</span>
        </template>
        <template v-slot:[`item.emailId`]="{ value }">
          <v-btn v-if="value" size="small" variant="text" @click="open(value)">Vaata kirja</v-btn>
        </template>
      </v-data-table>
      <div v-if="blockedNext" class="text-center pt-4">
        <v-btn variant="outlined" :loading="blockedLoading" @click="loadBlocked(true)">Laadi veel</v-btn>
      </div>
    </card>

    <!-- One e-mail, as it went out. The HTML is shown in a sandboxed frame with
         no scripts and no access to this page. -->
    <v-dialog v-model="dialog" max-width="900" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <span class="text-truncate">{{ email ? (email.subject || '(teemata)') : 'E-kiri' }}</span>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" aria-label="Sulge" @click="dialog = false" />
        </v-card-title>
        <v-card-text>
          <div v-if="emailLoading" class="py-8 text-center"><v-progress-circular indeterminate /></div>
          <v-alert v-else-if="emailError" type="warning" variant="tonal" density="compact">{{ emailError }}</v-alert>
          <template v-else-if="email">
            <dl class="meta">
              <dt>Saadetud</dt><dd>{{ formatDate(email.createdAt) }}</dd>
              <dt>Olek</dt><dd><v-chip size="x-small" variant="flat" :color="eventState(email.lastEvent).color">{{ eventState(email.lastEvent).text }}</v-chip></dd>
              <dt>Saatja</dt><dd>{{ email.from }}</dd>
              <dt>Saaja</dt><dd>{{ email.to.join(', ') }}</dd>
              <template v-if="email.cc.length"><dt>Koopia</dt><dd>{{ email.cc.join(', ') }}</dd></template>
              <template v-if="email.bcc.length"><dt>Pimekoopia</dt><dd>{{ email.bcc.join(', ') }}</dd></template>
              <template v-if="email.replyTo.length"><dt>Vastus</dt><dd>{{ email.replyTo.join(', ') }}</dd></template>
              <template v-if="email.messageId"><dt>Message-ID</dt><dd class="text-body-2">{{ email.messageId }}</dd></template>
            </dl>
            <div v-if="email.attachments.length" class="mt-3">
              <div class="text-subtitle-2">Manused</div>
              <div v-for="a in email.attachments" :key="a.id" class="d-flex align-center ga-2 mt-1">
                <v-icon size="small">{{ a.contentType === 'application/pdf' ? 'mdi-file-pdf-box' : 'mdi-paperclip' }}</v-icon>
                <span>{{ a.filename }}</span>
                <span class="text-body-2 text-grey-darken-1">{{ size(a.size) }}</span>
                <v-btn v-if="a.url && String(a.url).startsWith('https://')" size="small" variant="text" :href="a.url" target="_blank" rel="noopener noreferrer">Ava</v-btn>
              </div>
            </div>
            <v-tabs v-model="bodyTab" density="compact" class="mt-4">
              <v-tab value="html" :disabled="!email.html">Kiri</v-tab>
              <v-tab value="text" :disabled="!email.text">Tekst</v-tab>
            </v-tabs>
            <iframe
              v-if="bodyTab === 'html' && email.html"
              class="mail-frame"
              sandbox=""
              referrerpolicy="no-referrer"
              :srcdoc="email.html"
              title="E-kirja sisu"
            />
            <pre v-else-if="bodyTab === 'text' && email.text" class="mail-text">{{ email.text }}</pre>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </system-frame>
</template>

<script>
import SystemFrame from '@/components/SystemFrame.vue'
import api from '@/api'
import dayjs from 'dayjs'

// Resend's last_event values in the office's words, with a colour for the chip.
const EVENTS = {
  delivered: { text: 'Kohale jõudnud', color: 'green-darken-1' },
  opened: { text: 'Avatud', color: 'teal-darken-1' },
  clicked: { text: 'Klikitud', color: 'teal-darken-2' },
  sent: { text: 'Saadetud', color: 'blue-grey-lighten-1' },
  queued: { text: 'Järjekorras', color: 'grey-lighten-1' },
  scheduled: { text: 'Ajastatud', color: 'grey-lighten-1' },
  delivery_delayed: { text: 'Viibib', color: 'orange-darken-2' },
  bounced: { text: 'Põrkus', color: 'red-darken-1' },
  failed: { text: 'Ebaõnnestus', color: 'red-darken-1' },
  complained: { text: 'Rämpspost', color: 'red-darken-3' },
  suppressed: { text: 'Blokeeritud', color: 'red-darken-3' },
  canceled: { text: 'Tühistatud', color: 'grey' }
}
// Always offered in the filter, even at zero.
const COMMON = ['delivered', 'opened', 'bounced', 'failed', 'complained', 'delivery_delayed']
const MAX_AUTO_REQUESTS = 25

export default {
  components: { SystemFrame },
  data () {
    return {
      health: null,
      healthLoading: false,
      healthError: '',
      // The Saatjad card, from intra's own mail_log.
      senders: null,
      from: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
      to: dayjs().format('YYYY-MM-DD'),
      metrics: null,
      metricsLoading: false,
      metricsError: '',
      hover: null,
      emails: [],
      next: null,
      loading: false,
      error: '',
      requestId: 0,
      statuses: [],
      loadAllRun: 0,
      blocked: [],
      blockedNext: null,
      blockedLoading: false,
      blockedError: '',
      dialog: false,
      email: null,
      emailLoading: false,
      emailError: '',
      bodyTab: 'html',
      headers: [
        { title: 'Saadetud', key: 'createdAt' },
        { title: 'Saaja', key: 'to', sortable: false },
        { title: 'Teema', key: 'subject' },
        { title: 'Olek', key: 'lastEvent' }
      ],
      blockedHeaders: [
        { title: 'Aadress', key: 'email' },
        { title: 'Põhjus', key: 'origin' },
        { title: 'Alates', key: 'createdAt' },
        { title: '', key: 'emailId', sortable: false, align: 'end' }
      ]
    }
  },
  computed: {
    shown () {
      return this.statuses.length ? this.emails.filter(e => this.statuses.includes(e.lastEvent)) : this.emails
    },
    statusOptions () {
      const count = {}
      for (const e of this.emails) count[e.lastEvent] = (count[e.lastEvent] || 0) + 1
      const keys = [...new Set([...Object.keys(EVENTS), ...Object.keys(count)])]
      return keys
        .filter(key => key && key !== 'null' && (count[key] || COMMON.includes(key) || this.statuses.includes(key)))
        .map(key => ({ value: key, text: this.eventState(key).text + ' (' + (count[key] || 0) + ')' }))
    },
    tiles () {
      const t = (this.metrics && this.metrics.totals) || {}
      const n = key => (t[key] === undefined || t[key] === null) ? '—' : this.count(t[key])
      const rate = key => (t[key] === undefined || t[key] === null) ? null : String(Number(t[key]).toFixed(1)).replace('.', ',') + '%'
      return [
        { label: 'Saadetud', value: n('sent') },
        { label: 'Kohale jõudnud', value: n('delivered'), note: rate('delivery_rate') },
        { label: 'Põrkus', value: n('bounced'), note: t.bounced ? 'pehme ' + n('bounced_transient') + ' · kõva ' + n('bounced_permanent') : rate('bounce_rate') },
        { label: 'Ebaõnnestus', value: n('failed') },
        { label: 'Blokeeritud', value: n('suppressed') },
        { label: 'Avatud', value: n('unique_opened'), note: rate('open_rate') },
        { label: 'Klikitud', value: n('unique_clicked'), note: rate('click_rate') },
        { label: 'Rämpspost', value: n('complained'), note: rate('complaint_rate') }
      ]
    },
    // The per-day columns, laid out in SVG units: one scale for every mark and
    // tick, bars no thicker than 24, a label on every Nth day so they never collide.
    chart () {
      const rows = (this.metrics && this.metrics.days) || []
      const width = 720
      const height = 190
      const left = 40
      const top = 24
      const base = height - 26
      const slot = rows.length ? (width - 8 - left) / rows.length : 0
      const max = Math.max(1, ...rows.map(r => Number(r.sent) || 0))
      const step = this.niceStep(max)
      const topValue = Math.ceil(max / step) * step
      const y = v => base - (v / topValue) * (base - top)
      const ticks = []
      for (let v = 0; v <= topValue; v += step) ticks.push({ value: v, y: y(v) })
      return {
        width,
        height,
        left,
        top,
        base,
        slot,
        bar: Math.min(24, Math.max(2, slot - 2)),
        labelEvery: rows.length ? Math.max(1, Math.ceil(rows.length / 10)) : 0,
        ticks,
        days: rows.map((r, i) => ({
          period: r.period,
          sent: Number(r.sent) || 0,
          delivered: Number(r.delivered) || 0,
          problems: (Number(r.bounced) || 0) + (Number(r.failed) || 0),
          x: left + slot * (i + 0.5),
          y: y(Number(r.sent) || 0)
        }))
      }
    },
    tipStyle () {
      const day = this.chart.days[this.hover]
      if (!day) return {}
      const pct = day.x / this.chart.width * 100
      return { left: 'min(max(' + pct + '%, 80px), calc(100% - 80px))' }
    }
  },
  watch: {
    statuses (now) {
      if (now.length) this.loadAll()
    }
  },
  mounted () {
    this.loadSenders()
    this.loadHealth()
    this.loadRange()
    this.loadBlocked(false)
  },
  methods: {
    async loadSenders () {
      try {
        this.senders = await api.getMailSenders()
      } catch (err) {
        // The card stays away; the rest of the page says what is wrong.
        this.senders = null
      }
    },
    senderName (provider) {
      return provider === 'brevo' ? 'Brevo' : 'Resend'
    },
    formatTime (value) {
      return value ? dayjs(value).format('DD.MM.YYYY HH:mm') : ''
    },
    async loadHealth () {
      this.healthLoading = true
      this.healthError = ''
      try {
        this.health = await api.getMailHealth()
      } catch (err) {
        this.health = null
        this.healthError = (err && err.message) || 'Resendi andmete küsimine ebaõnnestus'
      } finally {
        this.healthLoading = false
      }
    },
    loadRange () {
      this.loadMetrics()
      this.load()
    },
    async loadMetrics () {
      if (!this.from || !this.to) return
      this.metricsLoading = true
      this.metricsError = ''
      try {
        this.metrics = await api.getMailMetrics({ from: this.from, to: this.to })
      } catch (err) {
        this.metrics = null
        this.metricsError = (err && err.message) || 'Kokkuvõtte küsimine ebaõnnestus'
      } finally {
        this.metricsLoading = false
      }
    },
    async load () {
      this.loadAllRun++
      this.emails = []
      this.next = null
      await this.fetchPage(null)
      if (this.statuses.length) await this.loadAll()
    },
    async loadMore () {
      if (this.next) await this.fetchPage(this.next)
    },
    // The rest of the chosen days, so the status filter sees all of them;
    // capped, past which the page says so and "Laadi veel" carries on.
    async loadAll () {
      const run = ++this.loadAllRun
      let requests = 0
      while (this.next && requests < MAX_AUTO_REQUESTS && run === this.loadAllRun && !this.error) {
        await this.fetchPage(this.next)
        requests++
      }
    },
    async fetchPage (next) {
      if (!this.from || !this.to) {
        this.error = 'Vali mõlemad kuupäevad'
        return
      }
      const id = ++this.requestId
      this.loading = true
      this.error = ''
      try {
        const answer = await api.getMailList({ from: this.from, to: this.to, ...(next ? { after: next.after } : {}) })
        if (id !== this.requestId) return
        this.emails = this.emails.concat(answer.emails || [])
        this.next = answer.next || null
      } catch (err) {
        if (id !== this.requestId) return
        this.error = (err && err.message) || 'E-kirjade küsimine ebaõnnestus'
      } finally {
        if (id === this.requestId) this.loading = false
      }
    },
    async loadBlocked (more) {
      this.blockedLoading = true
      this.blockedError = ''
      try {
        const answer = await api.getMailBlocked(more && this.blockedNext ? { after: this.blockedNext.after } : {})
        this.blocked = more ? this.blocked.concat(answer.addresses || []) : (answer.addresses || [])
        this.blockedNext = answer.next || null
      } catch (err) {
        this.blockedError = (err && err.message) || 'Blokeeritud aadresside küsimine ebaõnnestus'
      } finally {
        this.blockedLoading = false
      }
    },
    async open (id) {
      this.dialog = true
      this.email = null
      this.emailError = ''
      this.emailLoading = true
      try {
        this.email = await api.getMail(id)
        this.bodyTab = this.email.html ? 'html' : 'text'
      } catch (err) {
        this.emailError = (err && err.message) || 'E-kirja avamine ebaõnnestus'
      } finally {
        this.emailLoading = false
      }
    },
    // A rounded data-end on top, square at the baseline.
    barPath (x, y, w, h) {
      const r = Math.min(4, w / 2, h)
      return 'M' + x + ',' + (y + h) + 'V' + (y + r) + 'Q' + x + ',' + y + ' ' + (x + r) + ',' + y +
        'H' + (x + w - r) + 'Q' + (x + w) + ',' + y + ' ' + (x + w) + ',' + (y + r) + 'V' + (y + h) + 'Z'
    },
    niceStep (max) {
      const rough = max / 4
      const mag = Math.pow(10, Math.floor(Math.log10(rough || 1)))
      const step = [1, 2, 5, 10].map(m => m * mag).find(s => s >= rough) || mag * 10
      return Math.max(1, step)
    },
    domainState (status) {
      if (status === 'verified') return { text: 'Kinnitatud', color: 'green-darken-1', icon: 'mdi-check-circle' }
      if (status === 'failed' || status === 'temporary_failure') return { text: 'Viga', color: 'red-darken-1', icon: 'mdi-alert-circle' }
      if (status === 'pending') return { text: 'Kontrollimisel', color: 'orange-darken-2', icon: 'mdi-timer-sand' }
      return { text: 'Seadistamata', color: 'grey', icon: 'mdi-help-circle' }
    },
    eventState (event) {
      return EVENTS[event] || { text: event || '—', color: 'grey-lighten-1' }
    },
    originText (origin) {
      return { bounce: 'Põrkus', complaint: 'Rämpspost', manual: 'Käsitsi' }[origin] || origin || ''
    },
    pct (used, limit) {
      return limit ? Math.min(100, used / limit * 100) : 0
    },
    quotaColor (used, limit) {
      const p = this.pct(used, limit)
      return p >= 100 ? 'red-darken-1' : (p >= 80 ? 'orange-darken-2' : 'green-darken-1')
    },
    count (value) {
      return value === null || value === undefined ? '—' : Number(value).toLocaleString('et-EE')
    },
    size (bytes) {
      if (!bytes) return ''
      return bytes < 1024 * 1024 ? Math.round(bytes / 1024) + ' kB' : (bytes / 1024 / 1024).toFixed(1).replace('.', ',') + ' MB'
    },
    formatDate (date) {
      return date ? dayjs(date).format('DD.MM.YYYY HH:mm') : ''
    },
    shortDay (ymd) {
      return dayjs(ymd).format('DD.MM')
    },
    longDay (ymd) {
      return dayjs(ymd).format('DD.MM.YYYY')
    }
  }
}
</script>

<style scoped>
.num { font-variant-numeric: tabular-nums; }
.tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}
.tile {
  border: 1px solid rgba(0, 0, 0, .12);
  border-radius: 6px;
  padding: 10px 12px;
}
.tile-label { font-size: 13px; color: rgba(0, 0, 0, .6); }
.tile-value { font-size: 22px; font-weight: 600; line-height: 1.3; }
.tile-note { font-size: 12px; color: rgba(0, 0, 0, .6); }
.chart-wrap { position: relative; max-width: 760px; }
.chart { width: 100%; height: auto; display: block; }
.chart .grid { stroke: #e3e5e3; stroke-width: 1; }
.chart .baseline { stroke: #b9bdb9; stroke-width: 1; }
.chart .axis { fill: rgba(0, 0, 0, .6); font-size: 11px; font-variant-numeric: tabular-nums; }
.chart .bar { fill: #2a78d6; transition: opacity .1s; }
.chart .bar.dim { opacity: .45; }
.chart .problem { fill: #d03b3b; stroke: #fff; stroke-width: 2; }
.chart .problem-mark { fill: #fff; font-size: 10px; font-weight: 700; }
.problem-key {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d03b3b;
  margin-right: 6px;
  vertical-align: -1px;
}
.tip {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  background: #fff;
  border: 1px solid rgba(0, 0, 0, .15);
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .12);
  pointer-events: none;
  white-space: nowrap;
}
.mail-table :deep(tbody tr) { cursor: pointer; }
.meta {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 4px 12px;
  margin: 0;
}
.meta dt { color: rgba(0, 0, 0, .6); }
.meta dd { margin: 0; word-break: break-word; }
.mail-frame {
  width: 100%;
  height: 480px;
  border: 1px solid rgba(0, 0, 0, .12);
  border-radius: 4px;
  background: #fff;
  margin-top: 8px;
}
.mail-text {
  white-space: pre-wrap;
  font-family: inherit;
  border: 1px solid rgba(0, 0, 0, .12);
  border-radius: 4px;
  padding: 12px;
  margin-top: 8px;
  max-height: 480px;
  overflow: auto;
}
.failure-row {
  overflow-wrap: anywhere;
}
</style>
